import React, { useState, useEffect, useRef, useCallback } from 'react';
import { stockTransferAPI, storeAPI, itemAPI, unitAPI, stockAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ArrowLeftRight, 
  Search, 
  Loader2, 
  Check, 
  ChevronRight,
  Star,
  Clock,
  Package,
  Store,
  Hash,
  AlertCircle,
  RotateCcw,
  Zap,
  ArrowRight,
  Plus,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { formatNumber } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';

// Quick Transfer Steps
const STEPS = {
  SELECT_FROM: 1,
  SELECT_TO: 2,
  ADD_ITEMS: 3,
  CONFIRM: 4
};

export const QuickTransferPage = () => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(STEPS.SELECT_FROM);
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [fromStore, setFromStore] = useState(null);
  const [toStore, setToStore] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const searchInputRef = useRef(null);
  
  // Current item being added
  const [currentItem, setCurrentItem] = useState(null);
  const [currentQuantity, setCurrentQuantity] = useState('');
  const quantityInputRef = useRef(null);
  
  // Recent & Favorites
  const [recentItems, setRecentItems] = useState([]);
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    fetchData();
    loadLocalPreferences();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [storesRes, itemsRes, unitsRes, stockRes] = await Promise.all([
        storeAPI.getAll({ status: 'active' }),
        itemAPI.getAll({ status: 'active' }),
        unitAPI.getAll({ status: 'active' }),
        stockAPI.getAll()
      ]);
      setStores(storesRes.data);
      setItems(itemsRes.data);
      setUnits(unitsRes.data);
      setStock(stockRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadLocalPreferences = () => {
    try {
      const savedRecent = localStorage.getItem('quickTransfer_recentItems');
      const savedFavorites = localStorage.getItem('quickEntry_favorites');
      
      if (savedRecent) setRecentItems(JSON.parse(savedRecent));
      if (savedFavorites) setFavorites(JSON.parse(savedFavorites));
    } catch (e) {
      console.error('Error loading preferences:', e);
    }
  };

  const saveRecentItem = (item) => {
    const updated = [item, ...recentItems.filter(i => i.id !== item.id)].slice(0, 10);
    setRecentItems(updated);
    localStorage.setItem('quickTransfer_recentItems', JSON.stringify(updated));
  };

  const toggleFavorite = (item) => {
    const isFav = favorites.some(f => f.id === item.id);
    let updated;
    if (isFav) {
      updated = favorites.filter(f => f.id !== item.id);
    } else {
      updated = [...favorites, item];
    }
    setFavorites(updated);
    localStorage.setItem('quickEntry_favorites', JSON.stringify(updated));
    toast.success(isFav ? 'Removed from favorites' : 'Added to favorites');
  };

  const getAvailableStock = (itemId, storeId) => {
    const stockRecord = stock.find(s => s.item_id === itemId && s.store_id === storeId);
    return stockRecord?.quantity || 0;
  };

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    const lowerQuery = query.toLowerCase();
    const results = items.filter(item => 
      item.name.toLowerCase().includes(lowerQuery) ||
      item.code?.toLowerCase().includes(lowerQuery) ||
      item.category_name?.toLowerCase().includes(lowerQuery)
    ).slice(0, 10);
    
    setSearchResults(results);
  }, [items]);

  const handleFromStoreSelect = (store) => {
    setFromStore(store);
    setCurrentStep(STEPS.SELECT_TO);
  };

  const handleToStoreSelect = (store) => {
    if (store.id === fromStore.id) {
      toast.error("Cannot transfer to the same store");
      return;
    }
    setToStore(store);
    setCurrentStep(STEPS.ADD_ITEMS);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  const handleItemSelect = (item) => {
    setCurrentItem(item);
    saveRecentItem(item);
    setSearchQuery('');
    setSearchResults([]);
    setCurrentQuantity('');
    setTimeout(() => quantityInputRef.current?.focus(), 100);
  };

  const handleAddItem = () => {
    const qty = parseFloat(currentQuantity);
    const available = getAvailableStock(currentItem.id, fromStore.id);
    
    if (!qty || qty <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    
    if (qty > available) {
      toast.error(`Only ${formatNumber(available)} available in ${fromStore.name}`);
      return;
    }
    
    const newItem = {
      item_id: currentItem.id,
      item_name: currentItem.name,
      quantity: qty,
      unit_id: currentItem.default_unit_id,
      unit_name: currentItem.unit_name,
      available: available
    };
    
    const existingIndex = selectedItems.findIndex(i => i.item_id === currentItem.id);
    if (existingIndex >= 0) {
      const updated = [...selectedItems];
      updated[existingIndex] = newItem;
      setSelectedItems(updated);
    } else {
      setSelectedItems([...selectedItems, newItem]);
    }
    
    setCurrentItem(null);
    setCurrentQuantity('');
    toast.success(`Added ${currentItem.name}`);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  const handleRemoveItem = (index) => {
    setSelectedItems(selectedItems.filter((_, i) => i !== index));
  };

  const handleContinueToConfirm = () => {
    if (selectedItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    setCurrentStep(STEPS.CONFIRM);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      const payload = {
        property_id: fromStore.property_id,
        from_store_id: fromStore.id,
        to_store_id: toStore.id,
        items: selectedItems.map(item => ({
          item_id: item.item_id,
          quantity: item.quantity,
          unit_id: item.unit_id
        }))
      };
      
      await stockTransferAPI.create(payload);
      
      const totalQty = selectedItems.reduce((sum, item) => sum + item.quantity, 0);
      
      toast.success(
        <div className="space-y-1">
          <p className="font-semibold">Stock Transfer Successful!</p>
          <p className="text-sm">Items: {selectedItems.length}</p>
          <p className="text-sm">Total Qty: {formatNumber(totalQty)}</p>
          <p className="text-sm">{fromStore.name} → {toStore.name}</p>
        </div>,
        { duration: 5000 }
      );
      
      handleReset();
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to transfer stock';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setCurrentStep(STEPS.SELECT_FROM);
    setFromStore(null);
    setToStore(null);
    setSelectedItems([]);
    setCurrentItem(null);
    setCurrentQuantity('');
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleBack = () => {
    if (currentStep === STEPS.SELECT_TO) {
      setCurrentStep(STEPS.SELECT_FROM);
      setFromStore(null);
    } else if (currentStep === STEPS.ADD_ITEMS) {
      setCurrentStep(STEPS.SELECT_TO);
      setToStore(null);
    } else if (currentStep === STEPS.CONFIRM) {
      setCurrentStep(STEPS.ADD_ITEMS);
    }
  };

  const totalQuantity = selectedItems.reduce((sum, item) => sum + item.quantity, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full">
          <ArrowLeftRight className="h-5 w-5 text-blue-600" />
          <span className="font-semibold text-blue-700">Quick Stock Transfer</span>
        </div>
        <p className="text-muted-foreground">Move stock between stores</p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2">
        {[1, 2, 3, 4].map((step) => (
          <React.Fragment key={step}>
            <div 
              className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all",
                currentStep >= step 
                  ? "bg-blue-600 text-white" 
                  : "bg-muted text-muted-foreground"
              )}
            >
              {currentStep > step ? <Check className="h-5 w-5" /> : step}
            </div>
            {step < 4 && (
              <div className={cn(
                "w-12 h-1 rounded-full transition-all",
                currentStep > step ? "bg-blue-600" : "bg-muted"
              )} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step Labels */}
      <div className="flex justify-between text-xs text-muted-foreground px-2">
        <span className={currentStep >= 1 ? "text-blue-600 font-medium" : ""}>From</span>
        <span className={currentStep >= 2 ? "text-blue-600 font-medium" : ""}>To</span>
        <span className={currentStep >= 3 ? "text-blue-600 font-medium" : ""}>Items</span>
        <span className={currentStep >= 4 ? "text-blue-600 font-medium" : ""}>Confirm</span>
      </div>

      {/* Step 1: Select From Store */}
      {currentStep === STEPS.SELECT_FROM && (
        <Card data-testid="quick-transfer-from-step">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Store className="h-5 w-5" />
              Transfer From
            </CardTitle>
            <CardDescription>Select source store</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              {stores.map((store) => (
                <Button
                  key={store.id}
                  variant="outline"
                  className="w-full h-12 justify-start gap-3"
                  onClick={() => handleFromStoreSelect(store)}
                  data-testid={`from-store-${store.id}`}
                >
                  <Store className="h-4 w-4" />
                  <div className="text-left flex-1">
                    <p className="font-medium">{store.name}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Select To Store */}
      {currentStep === STEPS.SELECT_TO && (
        <Card data-testid="quick-transfer-to-step">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <ArrowRight className="h-5 w-5" />
              Transfer To
            </CardTitle>
            <CardDescription>
              From <span className="font-medium text-foreground">{fromStore?.name}</span> → Select destination
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              {stores
                .filter(s => s.id !== fromStore?.id)
                .map((store) => (
                  <Button
                    key={store.id}
                    variant="outline"
                    className="w-full h-12 justify-start gap-3"
                    onClick={() => handleToStoreSelect(store)}
                    data-testid={`to-store-${store.id}`}
                  >
                    <Store className="h-4 w-4" />
                    <div className="text-left flex-1">
                      <p className="font-medium">{store.name}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </Button>
                ))}
            </div>
            
            <Button variant="ghost" onClick={handleBack} className="w-full">
              ← Back
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Add Items */}
      {currentStep === STEPS.ADD_ITEMS && (
        <Card data-testid="quick-transfer-items-step">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Select Items
            </CardTitle>
            <CardDescription>
              <span className="font-medium text-foreground">{fromStore?.name}</span>
              <ArrowRight className="h-4 w-4 inline mx-2" />
              <span className="font-medium text-foreground">{toStore?.name}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Added Items List */}
            {selectedItems.length > 0 && (
              <div className="space-y-2 p-3 rounded-lg bg-blue-50 border border-blue-200">
                <p className="text-xs font-medium text-blue-700 uppercase tracking-wide">
                  Items to Transfer ({selectedItems.length})
                </p>
                <div className="space-y-2">
                  {selectedItems.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                      <div>
                        <p className="font-medium text-sm">{item.item_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatNumber(item.quantity)} {item.unit_name}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500"
                        onClick={() => handleRemoveItem(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Item Entry Form */}
            {currentItem ? (
              <div className="space-y-4 p-4 rounded-lg border-2 border-dashed border-blue-300 bg-blue-50/50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{currentItem.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Available: <span className="font-semibold text-green-600">{formatNumber(getAvailableStock(currentItem.id, fromStore.id))}</span> {currentItem.unit_name}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setCurrentItem(null)}>
                    Cancel
                  </Button>
                </div>
                
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground">Quantity to Transfer</label>
                  <Input
                    ref={quantityInputRef}
                    type="number"
                    placeholder="Enter quantity"
                    value={currentQuantity}
                    onChange={(e) => setCurrentQuantity(e.target.value)}
                    className="h-14 text-xl text-center"
                    min="0"
                    step="0.01"
                    data-testid="transfer-quantity-input"
                  />
                  
                  {/* Quick quantity buttons */}
                  <div className="flex gap-2 justify-center">
                    {[1, 5, 10, 25].map((num) => (
                      <Button
                        key={num}
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentQuantity(num.toString())}
                        disabled={num > getAvailableStock(currentItem.id, fromStore.id)}
                      >
                        {num}
                      </Button>
                    ))}
                  </div>
                </div>
                
                {parseFloat(currentQuantity) > getAvailableStock(currentItem.id, fromStore.id) && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700">
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-sm">Exceeds available stock</span>
                  </div>
                )}
                
                <Button 
                  onClick={handleAddItem} 
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={!currentQuantity || parseFloat(currentQuantity) <= 0 || parseFloat(currentQuantity) > getAvailableStock(currentItem.id, fromStore.id)}
                  data-testid="add-transfer-item-btn"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add to Transfer
                </Button>
              </div>
            ) : (
              <>
                {/* Search Input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search items..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-10 h-12 text-lg"
                    autoFocus
                    data-testid="transfer-item-search"
                  />
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {searchResults.map((item) => {
                      const available = getAvailableStock(item.id, fromStore.id);
                      const isFav = favorites.some(f => f.id === item.id);
                      return (
                        <div
                          key={item.id}
                          className={cn(
                            "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                            available > 0 ? "hover:bg-muted/50" : "opacity-50 cursor-not-allowed"
                          )}
                          onClick={() => available > 0 && handleItemSelect(item)}
                        >
                          <Package className="h-5 w-5 text-muted-foreground" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{item.name}</p>
                            <p className="text-xs text-muted-foreground">{item.category_name}</p>
                          </div>
                          <div className="text-right">
                            <p className={cn(
                              "font-semibold",
                              available <= 0 ? "text-red-500" : "text-green-600"
                            )}>
                              {formatNumber(available)}
                            </p>
                            <p className="text-xs text-muted-foreground">available</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => { e.stopPropagation(); toggleFavorite(item); }}
                          >
                            <Star className={cn("h-4 w-4", isFav && "fill-yellow-400 text-yellow-400")} />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Favorites and Recent */}
                {!searchQuery && (favorites.length > 0 || recentItems.length > 0) && (
                  <div className="space-y-4">
                    {favorites.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Favorites</p>
                        <div className="flex flex-wrap gap-2">
                          {favorites.slice(0, 6).map((item) => {
                            const available = getAvailableStock(item.id, fromStore.id);
                            return (
                              <Button
                                key={item.id}
                                variant="outline"
                                size="sm"
                                className={cn("h-9", available <= 0 && "opacity-50")}
                                onClick={() => available > 0 && handleItemSelect(item)}
                                disabled={available <= 0}
                              >
                                <Star className="h-3 w-3 fill-yellow-400 text-yellow-400 mr-1" />
                                {item.name}
                                <span className="ml-1 text-xs text-muted-foreground">({formatNumber(available)})</span>
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={handleBack} className="flex-1">
                ← Back
              </Button>
              <Button 
                onClick={handleContinueToConfirm} 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={selectedItems.length === 0}
                data-testid="continue-to-confirm"
              >
                Continue ({selectedItems.length} items) →
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Confirm */}
      {currentStep === STEPS.CONFIRM && (
        <Card data-testid="quick-transfer-confirm-step">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Check className="h-5 w-5" />
              Confirm Transfer
            </CardTitle>
            <CardDescription>Review and confirm stock transfer</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary */}
            <div className="p-6 rounded-xl bg-gradient-to-br from-blue-100 to-blue-50 border border-blue-200 space-y-4">
              <div className="flex items-center justify-center gap-4 py-2">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">From</p>
                  <p className="font-semibold">{fromStore?.name}</p>
                </div>
                <ArrowRight className="h-6 w-6 text-blue-600" />
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">To</p>
                  <p className="font-semibold">{toStore?.name}</p>
                </div>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                <span className="text-muted-foreground">Total Items</span>
                <span className="font-semibold">{selectedItems.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Total Quantity</span>
                <span className="font-bold text-2xl text-blue-700">{formatNumber(totalQuantity)}</span>
              </div>
            </div>

            {/* Items List */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Items</p>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {selectedItems.map((item, index) => (
                  <div key={index} className="flex justify-between items-center p-2 rounded bg-muted/50">
                    <span className="font-medium">{item.item_name}</span>
                    <span className="font-semibold">{formatNumber(item.quantity)} {item.unit_name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleBack} className="flex-1">
                ← Edit
              </Button>
              <Button 
                onClick={handleSubmit} 
                className="flex-1 h-12 text-lg bg-blue-600 hover:bg-blue-700"
                disabled={submitting}
                data-testid="confirm-transfer-btn"
              >
                {submitting ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <ArrowLeftRight className="h-5 w-5 mr-2" />
                )}
                Transfer Stock
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions Footer */}
      <div className="flex justify-center gap-4 text-sm">
        <Button variant="ghost" size="sm" onClick={handleReset} data-testid="start-over-btn">
          <RotateCcw className="h-4 w-4 mr-1" />
          Start Over
        </Button>
      </div>
    </div>
  );
};

export default QuickTransferPage;
